/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

/* eslint-disable no-underscore-dangle */
import {
    ApolloClient,
    InMemoryCache,
    ApolloLink,
    HttpLink,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import Cookies from 'universal-cookie';
import mbpUtil from 'mbp-api-util';
import mbpLogger from 'mbp-logger';
import { genericProductPaginationContainer } from './helpers/paginationCachePolicyObjectGenerators';

export const GRAPHQL_ENV = mbpUtil.getEnv('APP_GRAPHQL_ENV')
    ? mbpUtil.getEnv('APP_GRAPHQL_ENV')
    : process.env.NODE_ENV || 'development';

export const buildQubitApolloClient = () => {
    const uri = 'https://api.qubit.com/placements/query';
    const link = new HttpLink({
        uri,
        headers: {
            'content-type': 'text/plain',
        },
    });
    const cache = new InMemoryCache();
    const namedLink = new ApolloLink((operation, forward) => {
        operation.setContext(() => ({
            uri: `${uri}?${operation.operationName}`,
        }));
        return forward ? forward(operation) : null;
    });

    const client = new ApolloClient({
        cache,
        link: ApolloLink.from([namedLink, link]),
    });
    return client;
};

const buildGraphQLClient = (props = {}) => {
    const {
        host = mbpUtil.getEnv('APP_WC_HOST'),
        ssrMode,
        initialState,
        siteVariant,
    } = props;

    const ssrGraphQLHost = mbpUtil.getEnv('APP_GRAPHQL_HOST_SSR') || 'aggregator.acquisition-services.svc.cluster.local:8080';
    const uri = (typeof window !== 'undefined')
        ? `https://${host}/r/api/aggregator/graphql` // Client-Side
        : `${mbpUtil.getEnv('APP_WC_PROTOCOL_SSR')}://${ssrGraphQLHost}${mbpUtil.getEnv('APP_API_PREFIX_SSR')}/aggregator/graphql`; // Server-Side

    const getShopperManagerCookie = () => {
        const SM_COOKIE = 'ShopperManagerEnterprise';
        const cookies = new Cookies();
        return cookies.get(SM_COOKIE) || 'NO_ShopperManagerEnterprise_COOKIE';
    };

    const customFetchSerializer = (url, options) => {
        const decodedURL = decodeURIComponent(url);
        const removeLineBreaks = decodedURL.replace(/\n/g, '');
        const reduceSpaces = removeLineBreaks.replace(/\s\s+/g, ' '); // many spaces -> one
        // split off query, only query needs to be encoded
        const [origin, query] = reduceSpaces.split(/=(.+)/);
        // remove any operationName and variable tags from end of query
        const [slimQuery, operationName, variables] = query.split('&');
        // The below takes a different approach based on the use of graphql variables, which require a operationName
        // otherwise we will remove the name and serialize further
        let cleanedURL = '';
        // if not using a named operation with variables
        if (!operationName.includes('operationName') && variables !== 'variables={}') {
            // get queryName
            const queryName = slimQuery.slice(0, slimQuery.indexOf('{'));
            // remove queryName from query string
            const denamedQuery = queryName.trim().length >= 1 ? slimQuery.split(queryName)[1] : slimQuery;
            // encode just query string, replace single instance of }} with a space to match sw
            const encodedQuery = encodeURIComponent(denamedQuery.replace('}}', '} }'));
            // proceed as normal with serialization
            cleanedURL = `${origin}=${encodedQuery}`;
            // if a query is using graphql variables eg. guided nav and findCategoryById
        } else {
            // remove variables= because = cannot be encoded but variables object needs to be
            const variablesObject = variables.split('=')[1];
            /*
                similar to non named queries
                    - need to replace a single instance of }}
                    - recombine query properly with operationName and variables
                    - encode variables object, but hardcode &variables= in order to be sent as is
            */
            cleanedURL = `${origin}=${encodeURIComponent(slimQuery.replace('}}', '} }'))}&${operationName}&variables=${encodeURIComponent(variablesObject)}`;
        }
        // proceed with fetch request
        return fetch(cleanedURL, {
            ...options,
            headers: {
                ...options.headers,
                'baggage-sessionid': getShopperManagerCookie(),
                sv: siteVariant,
            },
        });
    };

    const surrogateSet = new Set();
    const afterwareAddSurrogateKeys = new ApolloLink((operation, forward) => forward(operation).map((response) => {
        const context = operation.getContext();
        const { response: { headers } } = context;
        const surrogateKey = headers.get('surrogate-key');
        surrogateSet.add(surrogateKey);
        return response;
    }));

    const link = ApolloLink.from([
        onError(({ graphQLErrors, networkError }) => {
            if (graphQLErrors) {
                graphQLErrors.forEach(({ message, locations, path }) => mbpLogger.logError({
                    message: JSON.stringify(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`),
                    function: 'Apollo Client GQL Error',
                    appName: process.env.npm_package_name,
                    module: 'mbp-pwa-ui',
                }));
            }
            if (networkError) {
                mbpLogger.logError({
                    message: JSON.stringify(`[Network error]: ${networkError}`),
                    function: 'Apollo Client Network Error',
                    appName: process.env.npm_package_name,
                    module: 'mbp-pwa-ui',
                });
            }
        }),
        afterwareAddSurrogateKeys,
        new HttpLink({
            uri,
            useGETForQueries: true,
            fetch: customFetchSerializer,
        }),
    ]);

    const cache = new InMemoryCache({
        typePolicies: {
            Query: {
                fields: {
                    findProductsBySearchTerm: genericProductPaginationContainer(),
                    findCategoryById: genericProductPaginationContainer(),
                },
            },
        },
    }).restore(initialState || {});

    // const cache = new InMemoryCache().restore(initialState || {});

    // cache invalidation headers
    const getSurrogateKeys = () => [...Array.from(surrogateSet)];

    const apolloClient = new ApolloClient({
        link,
        cache,
        ...(ssrMode ? { ssrMode: true } : { ssrForceFetchDelay: 100000 }),
        connectToDevTools: true,
    });

    return { apolloClient, getSurrogateKeys };
};

export default buildGraphQLClient;
